import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RoomDetails } from "@/types/accommodation";
import {
  XMarkIcon,
  UsersIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { formatPrice, toTitleCase, formatImageUrl } from "@/lib/general";
import { BedIcon, CirclePlusIcon, CoffeeIcon, PlusIcon } from "lucide-react";
import { getFacilityIcon } from "@/lib/accommodation/facilityIcons";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface RoomDetailsModalProps {
  room: RoomDetails;
  isOpen: boolean;
  onClose: () => void;
  numNights: number;
  onBookNow: () => void;
}

// Preload image helper
const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = () => resolve();
    img.onerror = () => reject();
  });
};

const RoomDetailsModal: React.FC<RoomDetailsModalProps> = ({
  room,
  isOpen,
  onClose,
  numNights,
  onBookNow,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [preloadedImages, setPreloadedImages] = useState<Set<string>>(
    new Set()
  );
  const [isLoading, setIsLoading] = useState(true);
  const [showAllFacilities, setShowAllFacilities] = useState(false);

  // Preload first 5 images when modal opens
  useEffect(() => {
    if (isOpen && room.images.length > 0) {
      setIsLoading(true);
      const imagesToPreload = room.images.slice(0, 5);

      Promise.all(
        imagesToPreload.map((img) =>
          preloadImage(formatImageUrl(img, "high"))
            .then(() => {
              setPreloadedImages((prev) => new Set([...prev, img]));
            })
            .catch(() => {
              console.warn(`Failed to preload image: ${img}`);
            })
        )
      ).finally(() => {
        setIsLoading(false);
      });
    }
  }, [isOpen, room.images]);

  const nextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === room.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? room.images.length - 1 : prev - 1
    );
  };

  // Memoize bed type computation
  const formattedBedType = useMemo(() => {
    const bedName = room.bedTypes[0]?.name;
    return toTitleCase(
      bedName === "unknown" ? "Not Specified" : bedName || "Not Specified"
    );
  }, [room.bedTypes]);

  // Memoize meal plan computation
  const formattedMealPlan = useMemo(() => {
    if (!room.mealPlan) return "Not Specified";
    const { currency, price } = room.mealPlan;
    return `From ${formatPrice(currency, price)}`;
  }, [room.mealPlan]);

  // Memoize key details section
  const keyDetails = useMemo(
    () => (
      <div className="grid grid-cols-3 gap-3 pr-2">
        <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg">
          <UsersIcon className="w-4 h-4 text-neutral-500" />
          <div className="space-y-0.5">
            <p className="text-xs font-medium text-neutral-900">Occupancy</p>
            <p className="text-xs font-light text-neutral-600">
              Up to {room.occupancy.maxOccupancy} guests
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg">
          <BedIcon className="w-4 h-4 text-neutral-500" strokeWidth={1.5} />
          <div className="space-y-0.5">
            <p className="text-xs font-medium text-neutral-900">Bed Type</p>
            <p className="text-xs font-light text-neutral-600">
              {formattedBedType}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg">
          <CoffeeIcon className="w-4 h-4 text-neutral-500" strokeWidth={1.5} />
          <div className="space-y-0.5">
            <p className="text-xs font-medium text-neutral-900">Meal Plan</p>
            <p className="text-xs font-light text-neutral-600">
              {formattedMealPlan}
            </p>
          </div>
        </div>
      </div>
    ),
    [room.occupancy.maxOccupancy, formattedBedType, formattedMealPlan]
  );

  // Memoize image gallery
  const imageGallery = useMemo(
    () => (
      <div className="relative h-full bg-neutral-100 rounded-xl">
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div
              key="loader"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex items-center justify-center bg-neutral-100 rounded-xl"
            >
              <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
            </motion.div>
          ) : (
            <motion.img
              key={currentImageIndex}
              src={formatImageUrl(room.images[currentImageIndex] || "", "high")}
              alt={`${room.name} - Image ${currentImageIndex + 1}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0 w-full h-full object-cover rounded-xl"
            />
          )}
        </AnimatePresence>

        {room.images.length > 1 && !isLoading && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/50 hover:bg-white shadow-lg transition-colors"
            >
              <ChevronLeftIcon className="w-4 h-4 text-neutral-700" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/50 hover:bg-white shadow-lg transition-colors"
            >
              <ChevronRightIcon className="w-4 h-4 text-neutral-700" />
            </button>
            <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-black/40 backdrop-blur-sm rounded-full text-white/90 text-xs font-light tracking-wide">
              {currentImageIndex + 1}{" "}
              <span className="text-white/70 mx-1">of</span>{" "}
              {room.images.length}
            </div>
          </>
        )}

        {/* Hidden preloader for next image */}
        {room.images.length > 1 && (
          <div className="hidden">
            <img
              src={formatImageUrl(
                room.images[(currentImageIndex + 1) % room.images.length] || "",
                "high"
              )}
              alt="preload"
            />
          </div>
        )}
      </div>
    ),
    [room.images, currentImageIndex, room.name, isLoading]
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl p-0 gap-3 flex flex-row items-stretch justify-between overflow-hidden h-[400px]">
        {/* Left side - Image Gallery */}
        <div className="w-[40%] rounded-l-lg overflow-hidden p-3">
          {imageGallery}
        </div>

        {/* Right side - Content */}
        <div className="w-[60%] flex flex-col h-full">
          <DialogHeader className="shrink-0 py-4 border-b border-neutral-200">
            <DialogTitle className="text-lg font-medium text-neutral-900 truncate max-w-md">
              {room.name}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
            <div className="py-4 space-y-6">
              {keyDetails}

              <div>
                <h3 className="text-base font-medium text-neutral-900 mb-2">
                  About this room
                </h3>
                <p className="text-[13px] font-light leading-relaxed text-neutral-700">
                  {room.description}
                </p>
              </div>

              {room.facilities && room.facilities.length > 0 && (
                <div>
                  <h3 className="text-base font-medium text-neutral-900 mb-3">
                    Room Facilities
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {room.facilities
                      .slice(0, showAllFacilities ? undefined : 4)
                      .map((facility) => {
                        const facilityIcon = getFacilityIcon(facility.name);
                        const Icon = facilityIcon?.icon;
                        return (
                          <div
                            key={facility.id}
                            className="flex items-center gap-3 text-[13px] font-light text-neutral-700 py-1.5"
                          >
                            {Icon ? (
                              <Icon
                                className="w-4 h-4 text-neutral-500"
                                strokeWidth={1.5}
                              />
                            ) : (
                              <div className="w-1.5 h-1.5 rounded-full bg-neutral-400" />
                            )}
                            {facilityIcon?.displayName || facility.name}
                          </div>
                        );
                      })}
                  </div>
                  {room.facilities.length > 4 && (
                    <button
                      onClick={() => setShowAllFacilities(!showAllFacilities)}
                      className="text-indigo-600 hover:text-indigo-700 text-xs mt-2 transition-colors duration-200"
                    >
                      {showAllFacilities ? "Show less" : "Show more"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-neutral-200 py-2 pr-2">
            <div className="flex items-center justify-between bg-neutral-50 px-6 py-3 rounded-lg">
              <div className="flex flex-col gap-1">
                <p className="text-xs font-light text-neutral-600">
                  Total for {numNights} nights
                </p>
                <p className="text-sm font-medium text-neutral-900">
                  {formatPrice(room.price.currency, room.price.total)}
                </p>
              </div>
              <button
                onClick={onBookNow}
                className="inline-flex items-center px-4 py-2.5 bg-indigo-700 text-white text-xs font-medium rounded-full hover:bg-indigo-800 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CirclePlusIcon className="w-4 h-4 mr-2 -ml-1" />
                Add to Trip
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RoomDetailsModal;
